import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { CommunityGuidelinesModal } from '../../components/UI';
import { authApi } from '../../services/api';
import styles from './Login.module.css';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, updateUser } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [acceptingGuidelines, setAcceptingGuidelines] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const searchParams = new URLSearchParams(window.location.search);
  const ssoError = searchParams.get('error');
  const ssoErrorMessage = ssoError === 'microsoft_auth_failed' ? 'Microsoft authentication failed. Please try again.'
    : ssoError === 'account_deactivated' ? 'Your account is deactivated. Contact your administrator.'
    : ssoError === 'sso_failed' ? 'Single sign-on failed. Please try again.'
    : ssoError === 'no_email' ? 'Could not retrieve email from Microsoft account.'
    : ssoError === 'no_token' ? 'Authentication failed. Please try again.'
    : ssoError === 'load_failed' ? 'Failed to load your profile. Please try again.'
    : ssoError ? 'Login failed. Please try again.'
    : null;

  const handleMicrosoftLogin = () => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    window.location.href = `${apiBaseUrl}/auth/microsoft`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    clearError();
    if (!email || !password) { setFormError('Please enter both email and password'); return; }
    try {
      const user = await login(email, password);
      if (!user.has_accepted_guidelines) { setShowGuidelines(true); } else { navigate('/dashboard'); }
    } catch (err: any) { setFormError(err.message || 'Login failed'); }
  };

  const handleAcceptGuidelines = async () => {
    try {
      setAcceptingGuidelines(true);
      await authApi.acceptGuidelines();
      updateUser({ has_accepted_guidelines: true });
      setShowGuidelines(false);
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to accept guidelines:', err);
      setFormError('Failed to accept guidelines. Please try again.');
    } finally {
      setAcceptingGuidelines(false);
    }
  };

  const errorContent = (formError || error || ssoErrorMessage) ? (
    <div className={isMobile ? styles.mobileError : styles.error}>
      {formError || error || ssoErrorMessage}
    </div>
  ) : null;

  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <div className={styles.mobileRipples}>
          <span /><span /><span />
        </div>
        <div className={styles.mobileAmbientCircle} />

        <div className={styles.mobileContent}>
          <div className={styles.mobileLogoRow}>
            <img src="/logo.svg" alt="RIPPLE" className={styles.mobileLogoImg} />
          </div>

          <div className={styles.mobileTaglineWrap}>
            <p className={styles.mobileTagline}>
              Every act of excellence, ethics and empathy{' '}
              <em className={styles.mobileTaglineAccent}>creates a ripple</em>.
            </p>
          </div>

          <div className={styles.glassCard}>
            <p className={styles.glassTagline}>Carpe Diem</p>
            <h2 className={styles.glassTitle}>Welcome back</h2>
            <p className={styles.glassDesc}>
              Sign in with your Matasma work account. No separate password to remember.
            </p>

            {errorContent}

            <button
              type="button"
              className={styles.mobileBtn}
              onClick={handleMicrosoftLogin}
            >
              <span className={styles.msLogo}>
                <span style={{ background: '#f25022' }} />
                <span style={{ background: '#7fba00' }} />
                <span style={{ background: '#00a4ef' }} />
                <span style={{ background: '#ffb900' }} />
              </span>
              Sign in with Microsoft
            </button>
          </div>

          <p className={styles.mobileFooter}>
            Trouble signing in?{' '}
            <a href="mailto:support@matasma.com" className={styles.mobileFooterLink}>Contact admin</a>
          </p>
        </div>

        <CommunityGuidelinesModal
          isOpen={showGuidelines}
          onAccept={handleAcceptGuidelines}
          isLoading={acceptingGuidelines}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.leftPanel}>
        <div className={styles.formWrapper}>
          <div className={styles.logo}>
            <img src="/logo.svg" alt="Ripple" className={styles.logoImage} />
          </div>

          <p className={styles.tagline}>Carpe Diem</p>
          <h1 className={styles.title}>Welcome back</h1>
          <p className={styles.description}>
            Sign in with your Matasma work account. No separate password to remember.
          </p>

          {errorContent}

          <button
            type="button"
            className={styles.microsoftButton}
            onClick={handleMicrosoftLogin}
          >
            <span className={styles.msLogo}>
              <span style={{ background: '#f25022' }} />
              <span style={{ background: '#7fba00' }} />
              <span style={{ background: '#00a4ef' }} />
              <span style={{ background: '#ffb900' }} />
            </span>
            Sign in with Microsoft
            <span className={styles.msArrow}>&#8594;</span>
          </button>

          <div className={styles.divider} />

          <p className={styles.helpText}>
            Trouble signing in? <a href="mailto:support@matasma.com" className={styles.helpLink}>Contact your administrator</a>
          </p>

          <p className={styles.copyright}>
            &copy; {new Date().getFullYear()} Matasma Digital Technologies LLP
          </p>
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.rippleEffect}>
          <span /><span /><span /><span /><span />
        </div>
        <div className={styles.decorCircle1} />
        <div className={styles.decorCircle2} />
        <div className={styles.quoteContainer}>
          <blockquote className={styles.quote}>
            Every act of excellence, ethics and empathy{' '}
            <em className={styles.quoteHighlight}>creates a ripple</em>.
          </blockquote>
        </div>
      </div>

      <CommunityGuidelinesModal
        isOpen={showGuidelines}
        onAccept={handleAcceptGuidelines}
        isLoading={acceptingGuidelines}
      />
    </div>
  );
};

export default Login;
